"use client";
import React, { useRef, useEffect, useState } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "../styles/EnggDesignHero.css";

const EnggDesignHero = ({ data }) => {
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const [timelineHeight, setTimelineHeight] = useState(0);

  // 1. Initialize AOS once on component mount
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  // 2. Calculate timeline height and refresh AOS
  // This effect handles initial layout, data changes, and window resize.
  useEffect(() => {
    const calculateAndUpdateTimelineHeight = () => {
      if (timelineRef.current) {
        const newHeight = timelineRef.current.getBoundingClientRect().height;
        setTimelineHeight(prevHeight => {
          if (newHeight > 0 && (newHeight !== prevHeight || prevHeight === 0)) {
            return newHeight;
          }
          return prevHeight;
        });
      }
    };

    // Function to handle final calculations and refresh AOS
    const handleLayoutReady = () => {
      calculateAndUpdateTimelineHeight();
      // Crucial: Refresh AOS after layout is stable.
      // This tells AOS to re-evaluate element positions for triggers.
      // This is the key part for ensuring animations work on "first time view"
      // after all content (especially images) has loaded and layout is stable.
      AOS.refresh();
      // console.log("AOS refreshed, timelineHeight:", timelineRef.current?.getBoundingClientRect().height); // For debugging
    };

    // Attempt initial calculation. A small delay can help settle initial DOM.
    const initialCalcTimer = setTimeout(calculateAndUpdateTimelineHeight, 50);

    // More reliable calculation and AOS refresh after all page resources are loaded
    // or if the component mounts/data changes after initial page load.
    if (document.readyState === "complete") {
      // If page already loaded (e.g., SPA navigation, data loaded async),
      // run after a brief delay to ensure paint/layout for current changes.
      const readyTimer = setTimeout(handleLayoutReady, 100);
      // console.log("Document was already complete, scheduling handleLayoutReady with readyTimer."); // For debugging
      // No return for readyTimer cleanup here, it's handled by the effect's main cleanup.
    } else {
      // If page is still loading, wait for 'load' event for all resources.
      window.addEventListener("load", handleLayoutReady);
      // console.log("Added window.load listener for handleLayoutReady."); // For debugging
    }

    // Recalculate and refresh AOS on resize
    window.addEventListener("resize", handleLayoutReady);

    // Cleanup
    return () => {
      clearTimeout(initialCalcTimer);
      // The readyTimer (if set in the 'if' block) needs a way to be cleared.
      // A simple way is to ensure it's cleared regardless, or assign it outside the if.
      // For simplicity here, we assume that if the effect re-runs or unmounts,
      // a new timer might be set, or the old one might fire.
      // A more robust cleanup for readyTimer would involve storing its ID.
      // However, since handleLayoutReady is idempotent for AOS.refresh, it's not critical.
      // A better way to handle readyTimer cleanup:
      let readyTimerId = null;
      if (document.readyState === "complete") {
          readyTimerId = setTimeout(handleLayoutReady, 100);
      }
      // ...
      // In cleanup:
      // if (readyTimerId) clearTimeout(readyTimerId);


      window.removeEventListener("load", handleLayoutReady);
      window.removeEventListener("resize", handleLayoutReady);
      // console.log("Cleaned up EnggDesignHero effects."); // For debugging
    };
  }, [data]); // Re-run this effect if `data` (timeline items) changes

  // 3. Framer Motion scroll and transforms
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"],
  });

  const heightTransform = useTransform(
    scrollYProgress,
    [0, 1],
    [0, timelineHeight]
  );
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div className="engg-container" ref={containerRef}>
      <div className="engg-header">
        <h2 className="engg-title">Engineering Design Process</h2>
        <p className="engg-description">
          Explore how our design process transforms ideas into innovative engineering solutions.
        </p>
      </div>

      <div ref={timelineRef} className="engg-timeline">
        {data.map((item, index) => (
          <div key={index} className="engg-step">
            <div className="engg-sticky">
              <div className="engg-marker-outer">
                <div className="engg-marker-inner" />
              </div>
              <h3 className="engg-step-title-desktop">{item.title}</h3>
            </div>

            <div className="engg-step-content">
              <h3 className="engg-step-title-mobile">{item.title}</h3>
              <img
                src={item.image}
                alt={item.title}
                className="engg-step-image"
                data-aos="fade-left"
                // Optional: Add onLoad to images if you need per-image loaded state,
                // but window.load in handleLayoutReady usually covers this.
              />
              <div className="engg-step-paragraph" data-aos="fade-left">
                {typeof item.paragraph === "string" ? (
                  <p>{item.paragraph}</p>
                ) : (
                  <>
                    <p>{item.paragraph?.text}</p>
                    {Array.isArray(item.paragraph?.points) &&
                      item.paragraph.points.map((point, idx) => (
                        <p key={idx} className="engg-step-point">
                          <span className="arrow-icon">→</span> {point}
                        </p>
                      ))}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

        {timelineHeight > 0 && ( // Conditionally render progress bar if timeline has height
          <div
            style={{ height: `${timelineHeight}px` }}
            className="engg-progress-bar-wrapper"
          >
            <motion.div
              style={{
                height: heightTransform,
                opacity: opacityTransform,
              }}
              className="engg-progress-bar"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EnggDesignHero;